import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

interface ContrastCheckerProps {
  bgColor: string;
  fgColor?: string;
  className?: string;
}

export function ContrastChecker({ bgColor, fgColor = '#ffffff', className }: ContrastCheckerProps) {
  useLanguage();
  const calculateContrast = (bg: string, fg: string): number => {
    const getLuminance = (hex: string): number => {
      // Remove # se presente
      hex = hex.replace('#', '');
      
      // Converte para RGB
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      
      // Aplica gamma correction
      const sRGB = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      
      // Calcula luminância relativa
      return 0.2126 * sRGB(r) + 0.7152 * sRGB(g) + 0.0722 * sRGB(b);
    };

    try {
      const l1 = getLuminance(bg);
      const l2 = getLuminance(fg);
      
      const lighter = Math.max(l1, l2);
      const darker = Math.min(l1, l2);
      
      return (lighter + 0.05) / (darker + 0.05);
    } catch {
      return 4.5; // fallback seguro
    }
  };

  const ratio = calculateContrast(bgColor, fgColor);
  const passes = ratio >= 4.5;

  if (passes) {
    return (
      <Alert className={className}>
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-xs">
          {translateInterfaceText("Contraste adequado:")}{" "}<strong>{ratio.toFixed(2)}:1</strong> {" "}{translateInterfaceText("(mínimo 4.5:1)")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="destructive" className={className}>
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription className="text-xs">
        <strong>{translateInterfaceText("Atenção:")}</strong> {" "}{translateInterfaceText("Contraste insuficiente (")}{ratio.toFixed(2)}{translateInterfaceText(":1). Recomendado: mínimo 4.5:1 para acessibilidade (WCAG AA).")}</AlertDescription>
    </Alert>
  );
}
